<template>
  <div class="sidebar">
    <ClientOnly>
      <div>
        <AppLogo class="mb-1" />
        <SidebarSideBarOption
          v-for="(option, index) in prefixOptions"
          :key="option.name"
          :data-intercom-target="option.dataTarget"
          :name="option.name"
          :display-text="$t(option.translationKey)"
          :tool-tip-text="$t(option.translationKey)"
          :icon="option.icon"
          :style="{ paddingTop: index === 0 ? '0' : '1.5rem' }"
          :selected-option="selectedData"
          @click="onClick(option)"
        />
      </div>
      <div>
        <SidebarSideBarOption
          v-for="option in suffixOptions"
          :key="option.name"
          :data-intercom-target="option.dataTarget"
          :name="option.name"
          :display-text="$t(option.translationKey)"
          :tool-tip-text="$t(option.translationKey)"
          :icon="option.icon"
          :selected-option="selectedData"
          @click="onClick(option)"
        />
      </div>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { useAuth0 } from "@auth0/auth0-vue";
import { onMounted, ref, Ref } from "vue";
import { navigateTo, useRuntimeConfig, useNuxtApp } from "nuxt/app";
import AppLogo from "./AppLogo.vue";
import routes from "~/constants/routes";
import { useNavigationStore } from "@/store/navigation";
import { useUserStore } from "@/store/user";
import { useOrgStore } from "@/store/org";
import { useLogout } from "~/composables/useLogout";
import { useApiCall } from "~/composables/useApiCall";
import type Emitter from "~/types/Emitter";

const navigationStore = useNavigationStore();
const selectedData: Ref = ref("");
const auth0 = useAuth0();
const userStore = useUserStore();
const user = process.client ? userStore.getUser : null;
const orgStore = useOrgStore();
const intercomHelpActive = ref(false);
const config = useRuntimeConfig();
const { $bus } = useNuxtApp() as unknown as { $bus: Emitter };

const populatePrefixOptions = () => {
  let prefixOptions = routes.filter((route) => route.isPrefix);

  // Filter based on FMS permissions
  prefixOptions = prefixOptions.filter((route) => {
    switch (route.name) {
      case "Reports":
        return orgStore.canAccessReports;
      case "Contacts":
        return orgStore.canAccessPrm;
      case "Manage":
        // For now, keep Manage accessible - could add specific checks for sub-routes
        return true;
      case "Monitor":
        return true;
      case "Social":
        return orgStore.hasSocialMediaAccess;
      default:
        return true;
    }
  });

  return prefixOptions.map((route) => {
    return {
      name: route.name,
      dataTarget: route.dataTarget,
      translationKey: route.translationKey,
      icon: route.icon,
    };
  });
};

const prefixOptions: Ref = ref(populatePrefixOptions());

const suffixOptions: Ref = ref(
  routes
    .filter((route) => !route.isPrefix)
    .map((route) => {
      let key = route.translationKey;
      if (route.name === "ExitUser") {
        if (user?.is_impersonated) {
          key = "sidebar.exituser";
        }
      }

      return {
        name: route.name,
        dataTarget: route.dataTarget,
        translationKey: key,
        icon: route.icon,
      };
    }),
);

const intercomHelp = () => {
  if (import.meta.client && window.Intercom && config.public.IS_PROD) {
    if (intercomHelpActive.value) {
      window.Intercom("hide");
      intercomHelpActive.value = false;
    } else {
      intercomHelpActive.value = true;
      window.Intercom("show");
    }
  } else {
    // eslint-disable-next-line no-console
    console.error("Intercom not available to show/hide");
  }
};

$bus.$on("leaveToManage", ({ path, menuItemUrl }) => {
  selectedData.value = path;
  navigationStore.setActiveNavBarGroup(selectedData.value);
  navigateTo(menuItemUrl, { external: false });
});

const onClick = async (opt: { name: string; displayText: string }) => {
  if (opt.name === "Help") {
    intercomHelp();
    return;
  }
  if (opt.name === "Contacts") {
    window.open(config.public.PRM_URL as string, "_blank");
    return;
  }
  if (opt.name === "ExitUser") {
    if (user?.is_impersonated) {
      await useApiCall(`/users/${user?.id}/end_impersonation`, {
        method: "PUT",
      });
    }
    useLogout(auth0);
    return;
  }

  selectedData.value = opt.name;
  navigationStore.setActiveNavBarGroup(selectedData.value);

  let navPath = navigationStore?.activeNavBarOptions?.[0].path;
  if (opt.name === "Settings" && user) {
    navPath = `${navPath}`;
  }

  const external = navigationStore?.activeNavBarOptions?.[0].isExternalRoute;

  if (
    ["Reports"].includes(opt.name) &&
    window.location.pathname.includes("/search")
  ) {
    $bus.$emit("leaveRoute", {
      navPath,
      external,
    });
    return;
  }

  await navigateTo(navPath, { external });
};

onMounted(() => {
  selectedData.value = navigationStore.activeSideBarOption;
});
</script>

<style scoped>
.sidebar {
  @apply h-full z-10 w-18 bg-primary-500 flex flex-col justify-between pb-5 fixed top-0;
}
</style>
